import {
  callPeerName,
  callPeerNumber,
  useSoftphone
} from "@dialstack/sdk-react/softphone";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import { neutral } from "app/themes/colors";
import ContactAvatar from "features/calls/rolodex/contact-avatar.component";
import { contactSubtitle } from "features/calls/rolodex/contact-kind.util";
import { ContactNameLink } from "features/calls/rolodex/contact-link.component";
import {
  CallContact,
  useGetOutboundPatientMatchQuery
} from "services/dialstack";

import { isColleagueLine, lastTenDigits } from "../phone-match.util";
import useIncomingCallPatients from "../use-incoming-call-patients.hook";
import ColleagueCallCard from "./colleague-call-card.component";
import { PLUM_600, callCardSx } from "./dialstack-softphone-widget.styles";
import SoftphonePatientRow from "./softphone-patient-row.component";
import useColleagueLookup from "./use-colleague-lookup.hook";
import { useDialstackSoftphone } from "./use-dialstack-softphone.hook";
import useTransferStatus from "./use-transfer-status.hook";

const MAX_VISIBLE_PATIENTS = 3;

// The Rolodex contact a hand-dialed number belongs to (ENG-2177): the same
// white inset card, naming the contact instead of a patient.
function OutgoingCallContact({
  label,
  contact,
  status
}: {
  label: string;
  contact: CallContact;
  status: string | null;
}) {
  return (
    <Box data-testid="outgoing-call-contact-match" sx={callCardSx}>
      <Typography
        data-testid={status ? "call-transfer-status" : undefined}
        sx={{ fontSize: 12, color: neutral[800] }}
      >
        {status ?? label}
      </Typography>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ py: 0.5 }}>
        <ContactAvatar contact={contact} size={28} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <ContactNameLink
            contact={contact}
            fontSize={14}
            color={PLUM_600}
            testId="outgoing-call-contact-link"
          />
          <Typography sx={{ fontSize: 12, color: neutral[800] }}>
            {contactSubtitle(contact)}
          </Typography>
        </Box>
      </Stack>
    </Box>
  );
}

// One outbound call's patient context. The patient comes from the click
// context when the dial originated from a patient surface; hand-typed keypad
// dials fall back to a backend number match (same last-10 semantics as
// inbound matching). A number no patient owns may still be a contact's.
function OutgoingCallPatients({
  label,
  number,
  clickPatientId,
  status
}: {
  label: string;
  number: string;
  clickPatientId?: number;
  status: string | null;
}) {
  const { data: match } = useGetOutboundPatientMatchQuery(
    { number },
    { skip: clickPatientId !== undefined || !number }
  );
  const patientIds =
    clickPatientId !== undefined
      ? [clickPatientId]
      : (match?.patient_ids ?? []);
  const { patients } = useIncomingCallPatients(patientIds);

  if (patients.length === 0) {
    const contact = clickPatientId === undefined ? match?.contact : null;
    return contact ? (
      <OutgoingCallContact label={label} contact={contact} status={status} />
    ) : null;
  }

  const visible = patients.slice(0, MAX_VISIBLE_PATIENTS);
  const hiddenCount = patients.length - visible.length;

  // The same white inset card the incoming-call patient match uses.
  return (
    <Box data-testid="outgoing-call-patient-match" sx={callCardSx}>
      <Typography
        data-testid={status ? "call-transfer-status" : undefined}
        sx={{ fontSize: 12, color: neutral[800] }}
      >
        {status ?? label}
      </Typography>
      {visible.map((patient) => (
        <SoftphonePatientRow key={patient.id} patient={patient} />
      ))}
      {hiddenCount > 0 && (
        <Typography sx={{ fontSize: 12, color: neutral[800] }}>
          +{hiddenCount} more patient{hiddenCount > 1 ? "s" : ""} share this
          number
        </Typography>
      )}
    </Box>
  );
}

// Which call the card belongs to, once a second call can sit beside it. A
// merged leg is neither current nor held: everyone is in the one conversation.
function outboundCallLabel(state: string, inConference: boolean) {
  if (inConference) return "In conference";
  if (state === "active") return "Current call";
  return state === "held" ? "On hold" : "Calling";
}

// Names who is on the other end of outbound calls. The SDK's in-call screen
// can only name inbound callers (platform caller-ID), so this card supplies
// the outbound context above it: the patient for an outside number, the
// colleague for an extension. One card per live outbound call (multi-call safe).
//
// `callIds` narrows the cards to those calls: the workbench shows the one
// call it is for (ENG-2226).
export default function OutgoingCallPatientCard({
  callIds
}: {
  callIds?: string[];
} = {}) {
  const { calls, consultCall, mergedCalls } = useSoftphone();
  const { outboundCallPatients } = useDialstackSoftphone();
  const transferStatus = useTransferStatus();

  const outbound = calls.filter(
    (call) =>
      call.direction === "outbound" &&
      (callIds === undefined || callIds.includes(call.id))
  );
  // SDK 3.3.0 three-way calling: the legs bridged into one conversation.
  const inConference = (callId: string) =>
    (mergedCalls ?? []).some((merged) => merged.id === callId);
  const lookupColleague = useColleagueLookup(outbound.map(callPeerNumber));

  return (
    <>
      {outbound.map((call) => {
        const peerNumber = callPeerNumber(call);
        if (isColleagueLine(peerNumber)) {
          const colleague = lookupColleague(peerNumber, callPeerName(call));
          return (
            colleague && (
              <ColleagueCallCard
                key={call.id}
                caption={
                  inConference(call.id)
                    ? "In conference"
                    : call.id === consultCall?.id
                      ? "Consulting"
                      : "Calling"
                }
                colleague={colleague}
              />
            )
          );
        }
        const number = lastTenDigits(peerNumber);
        const clickPatientId = outboundCallPatients[number]?.id;
        // Anything shorter than a full number (a service code, a partial
        // dial) cannot match a patient, so it is not worth a request.
        if (number.length < 10 && clickPatientId === undefined) return null;
        return (
          <OutgoingCallPatients
            key={call.id}
            label={outboundCallLabel(call.state, inConference(call.id))}
            number={number}
            clickPatientId={clickPatientId}
            status={transferStatus(call.id)}
          />
        );
      })}
    </>
  );
}
